import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import UserForm from './UserForm';
import FilterSidebar from '../FilterSidebar';
import ConfirmationModal from '../ConfirmationModal';

const UserManagement = () => {
  const { isDarkMode } = useTheme();
  const { showNotification } = useNotification();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isImportingCSV, setIsImportingCSV] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, userId: null, isBulk: false });
  
  // Filter and sort state
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [sortBy, setSortBy] = useState('username');

  useEffect(() => {
    fetchUsers();
  }, []);

  // Get unique roles from users
  const roles = useMemo(() => {
    const roleMap = {};
    users.forEach(user => {
      if (user.role) {
        roleMap[user.role] = (roleMap[user.role] || 0) + 1;
      }
    });
    
    // Add "All" option at the beginning
    const allRoles = [
      { value: 'ALL', label: 'All', count: users.length }
    ];
    
    // Add specific roles
    Object.entries(roleMap).forEach(([role, count]) => {
      allRoles.push({
        value: role,
        label: role,
        count
      });
    });
    
    return allRoles;
  }, [users]);

  // Sort options
  const sortOptions = [
    { value: 'username', label: 'Username' },
    { value: 'email', label: 'Email' },
    { value: 'role', label: 'Role' },
    { value: 'created', label: 'Date Created' }
  ];

  // Comprehensive filtering and sorting for users
  const processedUsers = useMemo(() => {
    if (!users) return [];
    
    let filtered = [...users];

    // Search filter - only search by username and email
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
      );
    }

    // Role filter
    if (selectedRoles.length > 0 && !selectedRoles.includes('ALL')) {
      filtered = filtered.filter(user => 
        user.role && selectedRoles.includes(user.role)
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'username':
          return a.username.localeCompare(b.username);
        case 'email':
          return a.email.localeCompare(b.email);
        case 'role':
          return (a.role || '').localeCompare(b.role || '');
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [users, searchTerm, selectedRoles, sortBy]);

  useEffect(() => {
    setFilteredUsers(processedUsers);
  }, [processedUsers]);

  useEffect(() => {
    const selectableUsers = filteredUsers.filter(user => user.email !== 'god@heaven.com');
    setSelectAll(selectedUsers.length > 0 && selectedUsers.length === selectableUsers.length);
  }, [selectedUsers, filteredUsers]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/users');
      setUsers(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (userId) => {
    const user = users.find(u => u.id === userId);
    
    // Check if trying to delete the God user
    if (user && user.email === 'god@heaven.com') {
      setError('The God user cannot be deleted');
      showNotification('The God user cannot be deleted', 'error');
      return;
    }

    setDeleteConfirm({ isOpen: true, userId, isBulk: false });
  };

  const confirmDelete = async () => {
    const { userId, isBulk } = deleteConfirm;
    
    try {
      if (isBulk) {
        const selectedUserObjects = users.filter(user => selectedUsers.includes(user.id));
        const godUserSelected = selectedUserObjects.some(user => user.email === 'god@heaven.com');
        
        if (godUserSelected) {
          setError('Cannot delete the God user');
          showNotification('Cannot delete the God user', 'error');
          setDeleteConfirm({ isOpen: false, userId: null, isBulk: false });
          return;
        }

        const deletePromises = selectedUsers.map(id => 
          apiClient.delete(`/users/${id}`)
        );
        await Promise.all(deletePromises);
        
        setUsers(users.filter(user => !selectedUsers.includes(user.id)));
        setSelectedUsers([]);
        setSelectAll(false);
        showNotification(`Successfully deleted ${selectedUsers.length} user(s)`, 'success');
      } else {
        await apiClient.delete(`/users/${userId}`);
        setUsers(users.filter(user => user.id !== userId));
        showNotification('User deleted successfully', 'success');
      }
    } catch (err) {
      const errorMessage = isBulk 
        ? 'Failed to delete some users' 
        : (err.response?.data?.error || 'Failed to delete user');
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error deleting user:', err);
    } finally {
      setDeleteConfirm({ isOpen: false, userId: null, isBulk: false });
    }
  };

  const handleEdit = (user) => {
    // Check if trying to edit the God user
    if (user.email === 'god@heaven.com') {
      setError('The God user cannot be modified');
      return;
    }
    
    setEditingUser(user);
    setShowForm(true);
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      ADMIN: '#dc3545',
      USER: '#28a745'
    };
    
    return (
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: roleColors[role] || '#6c757d'
        }}
      >
        {role}
      </span>
    );
  };

  const isGodUser = (user) => {
    return user.email === 'god@heaven.com';
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditingUser(null);
    fetchUsers();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const handleFormError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleRoleChange = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  const handleClearFilters = () => {
    setSelectedRoles([]);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedUsers(filteredUsers.filter(user => user.email !== 'god@heaven.com').map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedUsers.length === 0) {
      setError('Please select users to delete');
      return;
    }
    setDeleteConfirm({ isOpen: true, userId: null, isBulk: true });
  };

  const handleExportCSV = async () => {
    try {
      setIsExportingCSV(true);
      const response = await apiClient.get('/users/export/csv', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'users.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export CSV');
      console.error('Error exporting CSV:', err);
    } finally {
      setIsExportingCSV(false);
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setIsImportingCSV(true);
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await apiClient.post('/users/import/csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const { created, updated, errors } = response.data.data;
        let message = `Import completed: ${created} created, ${updated} updated`;
        const notificationType = errors.length > 0 ? 'warning' : 'success';
        if (errors.length > 0) {
          message += `, ${errors.length} error${errors.length > 1 ? 's' : ''}`;
        }
        showNotification(message, notificationType, 5000);
        fetchUsers(); // Refresh the list
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to import CSV';
      setError(errorMessage);
      showNotification(errorMessage, 'error', 5000);
      console.error('Error importing CSV:', err);
    } finally {
      setIsImportingCSV(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  if (loading) {
    return <div className="flex-center">Loading users...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ marginBottom: '1rem' }}>User Management</h2>
        <div className="flex gap-10" style={{ alignItems: 'center' }}>
          <button
            className="btn btn-success"
            onClick={handleExportCSV}
            disabled={isExportingCSV}
            style={{ 
              opacity: isExportingCSV ? 0.6 : 1,
              cursor: isExportingCSV ? 'not-allowed' : 'pointer'
            }}
          >
            {isExportingCSV ? 'Exporting...' : 'Export CSV'}
          </button>
          <label className="btn btn-info" style={{ cursor: 'pointer', margin: 0 }}>
            {isImportingCSV ? 'Importing...' : 'Import CSV'}
            <input
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              disabled={isImportingCSV}
              style={{ display: 'none' }}
            />
          </label>
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingUser(null);
              setShowForm(!showForm);
            }}
          >
            {showForm ? 'Cancel' : 'Add User'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {showForm && (
        <UserForm
          user={editingUser}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          onError={handleFormError}
        />
      )}

      {/* Main Content with Sidebar */}
      <div className="mcp-servers-layout">
        {/* Left Side: Filter Sidebar */}
        <div className="filter-sidebar-wrapper">
          <FilterSidebar
            categories={roles}
            selectedCategories={selectedRoles}
            onCategoryChange={handleRoleChange}
            onClearFilters={handleClearFilters}
            title="Filters"
          />
        </div>

        {/* Right Side: Search, Sort, and Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Search and Sort Controls */}
          <div className="search-sort-container" style={{
            backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
            borderRadius: '8px',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            marginBottom: '1.5rem'
          }}>
            {/* Search Element */}
            <div className="search-wrapper" style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#007bff',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by username or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                  color: isDarkMode ? 'white' : '#333',
                  fontSize: '0.9rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#007bff';
                  e.target.style.boxShadow = '0 0 0 2px rgba(0, 123, 255, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Sort Element - Anchored to the right */}
            <div className="sort-wrapper" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              marginLeft: 'auto'
            }}>
              <span style={{
                color: isDarkMode ? '#aaa' : '#666',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                Showing {filteredUsers.length} of {users.length}
              </span>
              
              <div style={{ position: 'relative' }}>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                    borderRadius: '6px',
                    color: isDarkMode ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                    e.target.style.borderColor = '#007bff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#2a2a2a' : 'white';
                    e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                  }}
                >
                  Sort by {sortOptions.find(opt => opt.value === sortBy)?.label || 'username'}
                  <span>▼</span>
                </button>
                
                {/* Dropdown Menu */}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                  border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '150px',
                  marginTop: '0.25rem',
                  display: 'none' // This would be controlled by state in a real implementation
                }}>
                  {sortOptions.map((option) => (
                    <div
                      key={option.value}
                      style={{
                        padding: '0.75rem 1rem',
                        cursor: 'pointer',
                        color: isDarkMode ? 'white' : '#333',
                        fontSize: '0.9rem',
                        borderBottom: `1px solid ${isDarkMode ? '#333' : '#f0f0f0'}`,
                        transition: 'background-color 0.2s ease'
                      }}
                      onClick={() => handleSortChange(option.value)}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {filteredUsers.length === 0 ? (
            <div className="card">
              <p>{users.length === 0 ? 'No users found.' : 'No matching users found.'}</p>
            </div>
          ) : (
            <div>
              {/* Selection Controls */}
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div className="flex-between" style={{ alignItems: 'center' }}>
                  <div className="flex" style={{ alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <label style={{ margin: 0, fontWeight: '500' }}>
                      Select All ({filteredUsers.filter(user => user.email !== 'god@heaven.com').length} selectable)
                    </label>
                  </div>
                  <button
                    className="btn btn-danger"
                    onClick={handleBulkDelete}
                    disabled={selectedUsers.length === 0}
                    style={{ 
                      opacity: selectedUsers.length === 0 ? 0.5 : 1,
                      cursor: selectedUsers.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Delete Selected ({selectedUsers.length})
                  </button>
                </div>
              </div>

              <div className="card">
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table className="table" style={{ minWidth: '600px' }}>
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id} style={isGodUser(user) ? { backgroundColor: '#fff3cd', fontWeight: 'bold' } : {}}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={(e) => handleSelectUser(user.id, e.target.checked)}
                              disabled={isGodUser(user)}
                              style={{ marginRight: '8px' }}
                            />
                          </td>
                          <td>
                            {user.username}
                            {isGodUser(user) && (
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '10px', 
                                color: '#856404',
                                fontStyle: 'italic'
                              }}>
                                (Protected)
                              </span>
                            )}
                          </td>
                          <td>{user.email}</td>
                          <td>{getRoleBadge(user.role)}</td>
                          <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                          <td>
                            <div className="flex gap-10">
                              <button
                                className="btn btn-secondary"
                                style={{ 
                                  padding: '4px 8px', 
                                  fontSize: '12px',
                                  opacity: isGodUser(user) ? 0.5 : 1,
                                  cursor: isGodUser(user) ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => handleEdit(user)}
                                disabled={isGodUser(user)}
                                title={isGodUser(user) ? 'God user cannot be modified' : 'Edit user'}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-danger"
                                style={{ 
                                  padding: '4px 8px', 
                                  fontSize: '12px',
                                  opacity: isGodUser(user) ? 0.5 : 1,
                                  cursor: isGodUser(user) ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => handleDelete(user.id)}
                                disabled={isGodUser(user)}
                                title={isGodUser(user) ? 'God user cannot be deleted' : 'Delete user'}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, userId: null, isBulk: false })}
        onConfirm={confirmDelete}
        title={deleteConfirm.isBulk ? `Delete ${selectedUsers.length} User(s)?` : 'Delete User?'}
        message={deleteConfirm.isBulk 
          ? `Are you sure you want to delete ${selectedUsers.length} user(s)? This action cannot be undone.`
          : 'Are you sure you want to delete this user? This action cannot be undone.'}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonStyle="danger"
      />
    </div>
  );
};

export default UserManagement;
